import React, { useEffect, useState } from 'react'
import { GoogleMap, MarkerF, PolylineF, useJsApiLoader } from "@react-google-maps/api"
import { MarkerImage, MarkerLocation, WalkNote } from "../../Interfaces"
import { GOOGLE_MAPS_CONFIG } from '../../config/googleMapsConfig'

// Add debug logging
console.log('Google Maps Config:', {
  ...GOOGLE_MAPS_CONFIG,
  googleMapsApiKey: GOOGLE_MAPS_CONFIG.googleMapsApiKey ? 'API Key is set' : 'API Key is missing'
})

interface MapProps {
  name?: string
  zoom?: number
  setZoom?: (zoom: number) => void
  coordinates?: google.maps.LatLngLiteral | google.maps.LatLng
  handleLocationAdjustment?: Function
  setMarkers?: (markers: MarkerLocation[]) => void
  markers?: MarkerLocation[]
  walkNotes?: WalkNote[]
  walkImages?: MarkerImage[]
  handleNoteClick?: (notes: WalkNote[]) => void
  handleImageClick?: (images: MarkerImage[]) => void
  animate?: boolean
  onDoubleClick?: (latLng: google.maps.LatLngLiteral) => void
}

export default function Map({
  name,
  zoom = 15,
  setZoom,
  coordinates = { lat: 0, lng: 0 },
  handleLocationAdjustment,
  setMarkers,
  markers,
  walkNotes,
  walkImages,
  handleNoteClick,
  handleImageClick,
  animate = false,
  onDoubleClick
}: MapProps) {
  const { isLoaded } = useJsApiLoader(GOOGLE_MAPS_CONFIG)
  const [map, setMap] = useState<google.maps.Map>()
  const [position, setPosition] = useState<google.maps.LatLngLiteral | google.maps.LatLng>(coordinates)
  const [path, setPath] = useState<google.maps.LatLngLiteral[]>([])

  useEffect(() => {
    setPosition(coordinates)
  }, [coordinates])

  useEffect(() => {
    const validMarkers = (markers || []).filter(marker =>
      typeof marker.lat === 'number' &&
      typeof marker.lng === 'number'
    );

    const newPath = validMarkers.map(marker => ({
      lat: Number(marker.lat),
      lng: Number(marker.lng)
    })) as google.maps.LatLngLiteral[];

    setPath(newPath);
  }, [markers])

  useEffect(() => {
    if (map && setZoom) {
      map.setZoom(zoom)
    }
  }, [zoom, map, setZoom])

  const onLoad = (map: google.maps.Map) => {
    setMap(map)
  }

  const onUnmount = () => {
    setMap(undefined)
  }

  const containerStyle = {
    width: "100%",
    height: "100%"
  }

  if (!isLoaded) return null

  return (
    <GoogleMap
      mapContainerStyle={containerStyle}
      center={position}
      zoom={zoom}
      onLoad={onLoad}
      onUnmount={onUnmount}
      options={{
        gestureHandling: 'cooperative',
        disableDoubleClickZoom: true
      }}
      onDblClick={(e) => {
        if (onDoubleClick && e.latLng) {
          onDoubleClick({
            lat: e.latLng.lat(),
            lng: e.latLng.lng()
          })
        }
      }}
    >
      {markers?.map((marker) => (
        <MarkerF
          key={marker.id || marker.label}
          position={{ lat: marker.lat || 0, lng: marker.lng || 0 }}
          label={marker.label.toString()}
          draggable={true}
          onDragEnd={(e) => {
            if (e.latLng && setMarkers) {
              const updatedMarkers = markers.map(m => {
                if (m.label === marker.label) {
                  return {
                    ...m,
                    lat: e.latLng!.lat(),
                    lng: e.latLng!.lng()
                  }
                }
                return m
              })
              setMarkers(updatedMarkers)
            }
          }}
          onClick={() => {
            if (handleNoteClick && marker.notes) {
              handleNoteClick(marker.notes)
            }
            if (handleImageClick && marker.images) {
              handleImageClick(marker.images)
            }
          }}
        />
      ))}
      {path.length > 1 && (
        <PolylineF
          path={path}
          options={{
            strokeColor: "#FF0000",
            strokeOpacity: 1.0,
            strokeWeight: 2
          }}
        />
      )}
    </GoogleMap>
  )
}
